/**
 * Minimal, dependency-free markdown block renderer.
 *
 * ARCH-005 §2 names react-markdown + remark-gfm as the example pipeline, but
 * the frozen dependency set does not include them and adding a dependency
 * mid-build risks the install/build gate (and is outside the lean intent).
 * This renderer covers the block grammar deliverables actually use: ATX
 * headings, paragraphs, unordered/ordered lists, fenced code, blockquotes,
 * and inline **bold** / *italic* / `code` / [links](url). It does NOT render
 * raw HTML (the input is trusted operator/agent markdown, but not rendering
 * HTML keeps the surface XSS-safe by construction (text nodes only).
 *
 * If a richer markdown surface is needed later, swapping in react-markdown is
 * a localized change behind this one component.
 */

import type { ReactNode } from 'react';

export function Markdown({ source }: { source: string }) {
  return <div className="space-y-3">{renderBlocks(source)}</div>;
}

function renderBlocks(src: string): ReactNode[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line === undefined) break;

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Fenced code block
    if (line.trim().startsWith('```')) {
      const buf: string[] = [];
      i += 1;
      let fenceLine = lines[i];
      while (i < lines.length && fenceLine !== undefined && !fenceLine.trim().startsWith('```')) {
        buf.push(fenceLine);
        i += 1;
        fenceLine = lines[i];
      }
      i += 1; // skip closing fence
      out.push(
        <pre
          key={key++}
          className="overflow-x-auto rounded-md bg-[color:var(--color-linen)] p-3 text-xs text-[color:var(--color-navy)]"
        >
          <code>{buf.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // Heading
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = (h[1] ?? '').length;
      const cls =
        level === 1
          ? 'font-display text-3xl italic leading-tight'
          : level === 2
            ? 'font-display text-2xl italic leading-tight'
            : level === 3
              ? 'text-lg font-semibold'
              : 'text-base font-semibold';
      out.push(
        <p
          key={key++}
          className={`${cls} text-[color:var(--color-black)]`}
        >
          {inline(h[2] ?? '')}
        </p>,
      );
      i += 1;
      continue;
    }

    // Blockquote
    if (line.trimStart().startsWith('>')) {
      const buf: string[] = [];
      let quoteLine = lines[i];
      while (i < lines.length && quoteLine !== undefined && quoteLine.trimStart().startsWith('>')) {
        buf.push(quoteLine.replace(/^\s*>\s?/, ''));
        i += 1;
        quoteLine = lines[i];
      }
      out.push(
        <blockquote
          key={key++}
          className="border-l-2 border-[color:var(--color-rule)] pl-4 text-sm italic text-[color:var(--color-text-mute)]"
        >
          {inline(buf.join(' '))}
        </blockquote>,
      );
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      let ulLine = lines[i];
      while (i < lines.length && ulLine !== undefined && /^\s*[-*]\s+/.test(ulLine)) {
        items.push(ulLine.replace(/^\s*[-*]\s+/, ''));
        i += 1;
        ulLine = lines[i];
      }
      out.push(
        <ul
          key={key++}
          className="list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-[color:var(--color-black)]"
        >
          {items.map((it, idx) => (
            <li key={idx}>{inline(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      let olLine = lines[i];
      while (i < lines.length && olLine !== undefined && /^\s*\d+\.\s+/.test(olLine)) {
        items.push(olLine.replace(/^\s*\d+\.\s+/, ''));
        i += 1;
        olLine = lines[i];
      }
      out.push(
        <ol
          key={key++}
          className="list-decimal space-y-1 pl-5 text-[15px] leading-relaxed text-[color:var(--color-black)]"
        >
          {items.map((it, idx) => (
            <li key={idx}>{inline(it)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // Paragraph (consume until blank line)
    const buf: string[] = [];
    let pLine = lines[i];
    while (i < lines.length && pLine !== undefined && pLine.trim() !== '') {
      buf.push(pLine);
      i += 1;
      pLine = lines[i];
    }
    out.push(
      <p
        key={key++}
        className="text-[15px] leading-relaxed text-[color:var(--color-black)]"
      >
        {inline(buf.join(' '))}
      </p>,
    );
  }

  return out;
}

/**
 * Inline parser: **bold**, *italic*, `code`, [text](url). Returns text nodes
 * and elements only (no HTML injection).
 */
function inline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let rest = text;
  let key = 0;
  const pattern =
    /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)]+)\))/;

  while (rest.length > 0) {
    const m = pattern.exec(rest);
    if (!m) {
      nodes.push(rest);
      break;
    }
    if (m.index > 0) nodes.push(rest.slice(0, m.index));
    if (m[2]) {
      nodes.push(<strong key={key++}>{m[2]}</strong>);
    } else if (m[4]) {
      nodes.push(<em key={key++}>{m[4]}</em>);
    } else if (m[6]) {
      nodes.push(
        <code
          key={key++}
          className="rounded bg-[color:var(--color-linen)] px-1 py-0.5 text-xs"
        >
          {m[6]}
        </code>,
      );
    } else if (m[8] && m[9]) {
      const href = m[9];
      const safe = /^https?:\/\//i.test(href) ? href : '#';
      nodes.push(
        <a
          key={key++}
          href={safe}
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2"
        >
          {m[8]}
        </a>,
      );
    }
    rest = rest.slice(m.index + m[0].length);
  }

  return nodes;
}
